import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Gift, Loader2, Radio, Trophy } from 'lucide-react';
import { RpChestOpen } from '../components/rp/RpChestOpen';
import { RpRules } from '../components/rp/RpRules';
import { RpTopWinners } from '../components/rp/RpTopWinners';
import { RpAudioMenu } from '../components/rp/RpAudioMenu';
import { RpWinnersMarquee } from '../components/rp/RpWinnersMarquee';
import { RpWinnersFeed } from '../components/rp/RpWinnersFeed';
import { RpDaily } from '../components/rp/RpDaily';
import { RpQueueCard } from '../components/rp/RpQueueCard';
import { RpTasksButton } from '../components/rp/RpTasksButton';
import { useRpSocket } from '../components/rp/useRpSocket';
import { music, sfx } from '../components/rp/rpSounds';
import { preloadRpAssets } from '../components/rp/rpPreload';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('customer_token')}` } });
const Art = ({ id, className }) => <img src={`/rp/${id}.png`} alt={id} className={`${className} object-contain drop-shadow-[0_0_12px_rgba(251,191,36,.6)]`} data-testid={`rp-art-${id}`} />;
const fmt = (n) => Number(n || 0).toLocaleString('pt-BR');
const dataBr = (iso) => (iso ? new Date(iso).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '');
const countdown = (iso, now) => {
  const ms = Math.max(0, new Date(iso).getTime() - now);
  const d = Math.floor(ms / 864e5), h = Math.floor((ms % 864e5) / 36e5), m = Math.floor((ms % 36e5) / 6e4), s = Math.floor((ms % 6e4) / 1e3);
  return d > 0 ? `${d}d ${String(h).padStart(2, '0')}h ${String(m).padStart(2, '0')}m` : `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};
const BG = { backgroundImage: 'linear-gradient(180deg, rgba(20,10,46,.35) 0%, rgba(20,10,46,.55) 45%, rgba(20,10,46,.92) 100%), url(/rp/bg.webp)', backgroundSize: 'cover', backgroundPosition: 'center top' };
/* Mesmo padrão do OIL TYCOON: jogo mobile vertical (max-w-md); no desktop fica numa moldura de celular
   com a arte desfocada atrás; topo reserva a área da barra do sistema (hora/bateria) no PWA. */
const Shell = ({ children, testId }) => (
  <div className="min-h-screen bg-[#06030f] text-white" data-testid={testId}>
    <div className="pointer-events-none fixed inset-0 z-0 hidden md:block" aria-hidden="true" data-testid="rp-desktop-backdrop">
      <img src="/rp/bg.webp" alt="" className="h-full w-full scale-110 object-cover opacity-25 blur-2xl" />
      <div className="absolute inset-0 bg-[#06030f]/85" />
    </div>
    <div className="fixed inset-y-0 left-1/2 z-0 w-full max-w-md -translate-x-1/2" style={BG} aria-hidden="true" data-testid="rp-phone-bg" />
    <div className="pointer-events-none fixed inset-y-0 left-1/2 z-[45] hidden w-full max-w-md -translate-x-1/2 border-x border-fuchsia-900/50 shadow-[0_0_80px_rgba(0,0,0,0.95)] md:block" aria-hidden="true" data-testid="rp-phone-frame" />
    <div className="pwa-safe-top relative z-10 mx-auto w-full max-w-md px-4 pb-24 pt-4">{children}</div>
    <style>{`
      @keyframes rp-title-pulse { 0%,100% { transform: scale(1); filter: drop-shadow(0 0 10px rgba(192,132,252,.55)) drop-shadow(0 10px 26px rgba(0,0,0,.75)) } 50% { transform: scale(1.025); filter: drop-shadow(0 0 26px rgba(217,70,239,.95)) drop-shadow(0 0 48px rgba(163,230,53,.55)) drop-shadow(0 10px 26px rgba(0,0,0,.75)) } }
      .rp-title-pulse { animation: rp-title-pulse 3.2s ease-in-out infinite; will-change: transform, filter }
      @keyframes rp-marquee { 0% { transform: translateX(0) } 100% { transform: translateX(-50%) } }
      @keyframes rp-neon-card { 0%,100% { box-shadow: 0 0 10px rgba(192,132,252,.45), inset 0 0 10px rgba(192,132,252,.15) } 50% { box-shadow: 0 0 22px rgba(217,70,239,.9), 0 0 40px rgba(163,230,53,.35), inset 0 0 14px rgba(163,230,53,.2) } }
      .rp-neon-card { animation: rp-neon-card 2.4s ease-in-out infinite }
      @keyframes rp-chest-neon { 0%,100% { transform: translate(-50%,-50%) scale(.95); opacity: .55 } 50% { transform: translate(-50%,-50%) scale(1.12); opacity: 1 } }
      .rp-chest-neon { background: radial-gradient(circle, rgba(217,70,239,.75) 0%, rgba(168,85,247,.45) 35%, rgba(163,230,53,.25) 55%, transparent 72%); filter: blur(16px); animation: rp-chest-neon 2.6s ease-in-out infinite }
      @media (prefers-reduced-motion: reduce) { .rp-title-pulse, .rp-neon-card, .rp-chest-neon, [data-testid="rp-prizes-track"] { animation: none !important } }
    `}</style>
  </div>
);

/* RECARGA PREMIADA — 1 baú a cada N moedas (admin); prêmio = presente entregue pelo bot na live do cliente.
   Baús não abertos e prêmios não recebidos até o fim do evento são perdidos (regra aplicada no servidor). */
export default function RecargaPremiadaPage() {
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [erro, setErro] = useState('');
  const [busy, setBusy] = useState(false);
  const [anim, setAnim] = useState(null); // { result, error } enquanto o clipe do baú roda
  const [tab, setTab] = useState('feed'); // feed (todos | meus) | top
  const [notice, setNotice] = useState(null); // aviso DENTRO do card do evento: { kind: 'ok'|'err'|'warn', text }
  const say = useCallback((kind, text) => { setNotice({ kind, text }); if (kind !== 'ok') sfx.fail(); }, []);
  useEffect(() => { music.start(); return () => music.stop(); }, []);
  useEffect(() => { music.duck(!!anim); }, [anim]);
  const back = () => { sfx.close(); navigate('/conta'); };
  useEffect(() => {
    if (!notice) return undefined;
    const t = setTimeout(() => setNotice(null), notice.kind === 'ok' ? 6000 : 9000);
    return () => clearTimeout(t);
  }, [notice]);


  // só cliente LOGADO acessa o evento (mesmo padrão do OIL TYCOON): sem token ou sessão expirada → tela de login
  const load = useCallback(async () => {
    if (!localStorage.getItem('customer_token')) { navigate('/conta', { replace: true }); return; }
    try {
      const { data } = await axios.get(`${API}/rp/state`, auth());
      if (!data.me) { localStorage.removeItem('customer_token'); navigate('/conta', { replace: true }); return; } // sessão inválida/expirada
      setState(data);
    }
    catch (e) {
      if (e.response?.status === 401) { localStorage.removeItem('customer_token'); navigate('/conta', { replace: true }); return; }
      setErro('Não consegui carregar o evento.');
    }
  }, [navigate]);
  useEffect(() => { load(); }, [load]);
  // tempo real: servidor avisa (recarga, baú aberto, fila, entrega) → recarrega o estado na hora, sem F5
  const [feedTick, setFeedTick] = useState(0);
  const [flash, setFlash] = useState(null); // destaque curto no card quando chega recarga/tarefa
  useRpSocket(useCallback((type, data) => {
    load();
    if (type === 'rp.prize' || type === 'rp.queue') setFeedTick((t) => t + 1);
    if (type === 'rp.recharge') { sfx.claim(); setFlash(data.chests > 0 ? `+${data.chests} baú! Recarga de 🪙${fmt(data.coins)} contabilizada` : `Recarga de 🪙${fmt(data.coins)} contabilizada — progresso atualizado`); }
    if (type === 'rp.task_done') { sfx.claim(); setFlash(`Tarefa concluída! +${data.chests} baú extra`); }
  }, [load]));
  useEffect(() => { if (!flash) return undefined; const t = setTimeout(() => setFlash(null), 5000); return () => clearTimeout(t); }, [flash]);
  useEffect(() => { if (state?.event) preloadRpAssets(state.event.prizes.map((p) => p.prop_id)); }, [state?.event?.event_id]);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!state?.event?.upcoming) return undefined;
    const id = setInterval(() => {
      setNow(Date.now());
      if (new Date(state.event.starts_at).getTime() <= Date.now()) load(); // começou → servidor libera
    }, 1000);
    return () => clearInterval(id);
  }, [state, load]);
  useEffect(() => {
    const pend = state?.me?.queue || state?.me?.prizes?.some((p) => ['QUEUED', 'SENDING'].includes(p.status));
    if (!pend) return undefined;
    const id = setInterval(load, 3000);
    return () => clearInterval(id);
  }, [state, load]);

  const open = async () => {
    sfx.open();
    setBusy(true); setAnim({ result: null, error: null });
    try {
      const { data } = await axios.post(`${API}/rp/open`, { client_request_id: crypto.randomUUID() }, auth());
      if (!data.ok) setAnim({ result: null, error: data.error || 'Não foi possível abrir o baú.' });
      else setAnim({ result: data.prize, error: null });
      await load();
    } catch (e) { setAnim({ result: null, error: e.response?.data?.detail || 'Erro ao abrir o baú.' }); }
    setBusy(false);
  };
  const closeAnim = useCallback(() => { setAnim((a) => { if (a?.error) say('err', a.error); return null; }); }, [say]);

  const claim = async () => {
    sfx.click();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/rp/claim`, {}, auth());
      if (data.ok) { sfx.claim(); setNotice(null); }   // o card da fila (posição/cronômetro) já diz tudo — sem aviso repetido
      else if (data.in_queue) say('warn', data.error);
      else say('err', data.error);
      await load();
    } catch (e) { say('err', e.response?.data?.detail || 'Erro ao solicitar a entrega.'); }
    setBusy(false);
  };

  if (!state) return <div className="flex min-h-screen items-center justify-center bg-[#140a2e]"><Loader2 className="h-8 w-8 animate-spin text-fuchsia-300" /></div>;
  if (!state.event) {
    return (
      <Shell testId="rp-no-event">
        <div className="flex min-h-[80vh] flex-col items-center justify-center text-center">
        <img src="/rp/title.webp" alt="Recarga Premiada" className="w-full max-w-xs drop-shadow-[0_8px_24px_rgba(0,0,0,.7)]" />
        <h1 className="sr-only">RECARGA PREMIADA</h1>
        <p className="mt-3 inline-block rounded-full border border-rose-400/70 bg-rose-950/60 px-4 py-1 text-xs font-black uppercase tracking-[0.2em] text-rose-300" data-testid="rp-inactive-badge">Evento inativo</p>
        <p className="mt-3 text-sm text-purple-200/80">{erro || 'O evento não está disponível no momento.'}</p>
        <button onClick={back} className="mt-6 rounded-full border border-fuchsia-400/60 px-6 py-2 text-xs font-black text-fuchsia-200" data-testid="rp-back">← Voltar à minha conta</button>
        </div>
      </Shell>
    );
  }
  const { event: ev, me } = state;

  return (
    <Shell testId="rp-page">
      {anim && <RpChestOpen result={anim.result} error={anim.error} onClose={closeAnim} />}
      <div>
        <div className="flex items-center justify-between">
          <button onClick={back} className="flex items-center gap-1 rounded-full bg-black/40 px-3 py-1 text-xs font-bold text-purple-100 backdrop-blur" data-testid="rp-back"><ArrowLeft className="h-4 w-4" /> Minha conta</button>
          <div className="flex items-center gap-2">
            <RpAudioMenu />
            <RpRules event={ev} onOpen={() => sfx.click()} onClose={() => sfx.close()} />
          </div>
        </div>
        <img src="/rp/title.webp" alt="Recarga Premiada" className="rp-title-pulse mx-auto mt-1 w-full max-w-[320px]" data-testid="rp-title-art" />
        <h1 className="sr-only">RECARGA PREMIADA</h1>
        <p className="mt-3 rounded-2xl bg-black/45 px-3 py-2 text-center text-xs text-purple-100 backdrop-blur">A cada <b className="text-amber-300">{fmt(ev.coins_per_chest)} moedas</b> compradas você ganha um baú (até <b className="text-amber-300">{ev.max_chests_per_day}</b> por dia). Abra e receba o presente <b>na sua live</b>!</p>
        {ev.upcoming && (
          <div className="mt-3 rounded-2xl border border-lime-400/60 bg-[#140a2e]/80 px-3 py-3 text-center backdrop-blur shadow-[0_0_24px_rgba(163,230,53,.35)]" data-testid="rp-upcoming">
            <p className="text-[10px] font-black uppercase tracking-[.35em] text-lime-300 drop-shadow-[0_0_8px_rgba(163,230,53,.9)]">Em breve</p>
            <p className="mt-1 text-2xl font-black text-white drop-shadow-[0_0_12px_rgba(217,70,239,.8)]" data-testid="rp-countdown">{countdown(ev.starts_at, now)}</p>
            <p className="text-[10px] text-purple-200/80">Começa em {dataBr(ev.starts_at)} · termina em {dataBr(ev.ends_at)}</p>
            <p className="mt-1 text-[10px] text-purple-200/70">Recargas feitas antes do início não geram baú.</p>
          </div>
        )}
        {!ev.live && !ev.upcoming && <p className="mt-2 rounded-full border border-rose-400/70 bg-rose-950/70 px-3 py-1 text-center text-[10px] font-black tracking-widest text-rose-200" data-testid="rp-ended">EVENTO ENCERRADO — baús não abertos e prêmios não recebidos foram perdidos</p>}
        {ev.live && ev.ends_at && <p className="mt-2 text-center text-[10px] font-bold text-purple-200/80" data-testid="rp-ends-at">Termina em {dataBr(ev.ends_at)} · abra os baús e receba na live até lá</p>}

        {/* prêmios — carrossel neon contínuo; disponível (estoque KAKO − reservados) calculado no servidor */}
        <div className="relative mt-4 overflow-hidden py-2" data-testid="rp-prizes"
          style={{ maskImage: 'linear-gradient(90deg, transparent, black 8%, black 92%, transparent)', WebkitMaskImage: 'linear-gradient(90deg, transparent, black 8%, black 92%, transparent)' }}>
          <div className="flex w-max gap-2" style={{ animation: `rp-marquee ${Math.max(18, ev.prizes.length * 4.5)}s linear infinite` }} data-testid="rp-prizes-track">
            {[0, 1].map((dup) => ev.prizes.map((p, i) => {
              const out = p.available === 0;
              return (
                <div key={`${dup}-${p.prop_id}`} data-testid={dup === 0 ? `rp-prize-${p.prop_id}` : undefined} data-available={p.available ?? ''}
                  className={`relative w-[104px] shrink-0 rounded-2xl border p-2 text-center backdrop-blur ${out ? 'border-zinc-500/40 bg-zinc-800/70 grayscale' : 'rp-neon-card border-fuchsia-400/70 bg-gradient-to-b from-purple-900/85 to-[#140a2e]/95'}`}
                  style={out ? undefined : { animationDelay: `${i * 0.4}s` }}>
                  <p className={`text-[10px] font-black ${out ? 'text-zinc-400' : 'text-lime-300 drop-shadow-[0_0_6px_rgba(163,230,53,.9)]'}`}>{p.weight}%</p>
                  <Art id={p.prop_id} className={`mx-auto h-14 w-14 ${out ? 'opacity-40' : ''}`} />
                  <p className={`mt-1 text-[11px] font-black ${out ? 'text-zinc-400' : 'text-amber-300'}`}>🪙{fmt(p.coins)}</p>
                  {typeof p.available === 'number' && (
                    out
                      ? <p className="mt-0.5 rounded-full bg-zinc-700 px-1 text-[8px] font-black tracking-wider text-zinc-200" data-testid={dup === 0 ? `rp-prize-out-${p.prop_id}` : undefined}>ESGOTOU</p>
                      : <p className="mt-0.5 rounded-full bg-lime-400/20 px-1 text-[8px] font-black text-lime-300" data-testid={dup === 0 ? `rp-prize-stock-${p.prop_id}` : undefined}>{fmt(p.available)} disp.</p>
                  )}
                </div>
              );
            }))}
          </div>
        </div>

        {/* baú */}
        <div className="relative mt-5 rounded-3xl border border-amber-400/50 bg-[#140a2e]/70 p-4 text-center shadow-[0_0_30px_rgba(168,85,247,.35)] backdrop-blur" data-testid="rp-chest-card">
          {me && <RpTasksButton day={me.day} />}
          <div className="relative mx-auto mt-12 flex h-48 w-48 items-center justify-center" data-testid="rp-chest-visual">
            {/* aqui SEMPRE o baú fechado — o prêmio só aparece no card de resultado */}
            <div className="rp-chest-neon pointer-events-none absolute left-1/2 top-1/2 h-44 w-44 -translate-x-1/2 -translate-y-1/2 rounded-full" aria-hidden="true" />
            <img src="/rp/chest.webp" alt="Baú" className={`relative h-48 w-48 object-contain drop-shadow-[0_10px_18px_rgba(0,0,0,.6)] ${me?.chests_available > 0 && ev.live ? '' : 'grayscale-[.4]'}`} data-testid="rp-chest-art" />
          </div>
          <p className="mt-2 text-sm font-black" data-testid="rp-chests">{me ? `${me.chests_available} baú(s) para abrir` : 'Entre na sua conta para ver seus baús'}</p>
          {me && (
            <>
              <RpDaily day={me.day} perChest={ev.coins_per_chest} />
              {flash && <p className="mt-2 rounded-full border border-lime-400/70 bg-lime-500/20 px-3 py-1 text-[11px] font-black text-lime-100 shadow-[0_0_14px_rgba(163,230,53,.6)] animate-in fade-in zoom-in-95 duration-300" data-testid="rp-live-flash">{flash}</p>}
              <button onClick={open} disabled={busy || !ev.live || me.chests_available < 1} data-testid="rp-open-btn"
                className="mt-3 w-full rounded-full bg-gradient-to-r from-fuchsia-500 via-amber-400 to-lime-400 py-3 text-sm font-black text-black shadow-[0_0_18px_rgba(251,191,36,.6)] disabled:opacity-40">
                {busy && anim ? <Loader2 className="mx-auto h-5 w-5 animate-spin" /> : <><Gift className="mr-1 inline h-4 w-4" /> ABRIR BAÚ</>}
              </button>
              <RpQueueCard queue={me.queue} />
              {me.pending > 0 && me.queue?.status !== 'RUNNING' && (
                <button onClick={claim} disabled={busy} data-testid="rp-claim-btn"
                  className="mt-2 w-full rounded-full border-2 border-lime-400 bg-lime-500/25 py-3 text-sm font-black text-lime-200 shadow-[0_0_18px_rgba(163,230,53,.5)] disabled:opacity-40">
                  {busy ? <><Loader2 className="mr-1 inline h-4 w-4 animate-spin" /> ENTRANDO NA FILA…</>
                    : me.queue ? <><Radio className="mr-1 inline h-4 w-4 animate-pulse" /> ADICIONAR {me.pending} PRÊMIO(S) À ENTREGA</>
                      : <><Radio className="mr-1 inline h-4 w-4 animate-pulse" /> RECEBER {me.pending} PRÊMIO(S) NA LIVE</>}
                </button>
              )}
              {notice && (
                <div data-testid="rp-notice" data-kind={notice.kind}
                  className={`mt-2 rounded-2xl border px-3 py-2 text-[11px] font-bold ${notice.kind === 'ok' ? 'border-lime-400/70 bg-lime-500/15 text-lime-100' : notice.kind === 'warn' ? 'border-amber-400/70 bg-amber-500/15 text-amber-100' : 'border-rose-400/70 bg-rose-600/20 text-rose-100'}`}>
                  {notice.text}
                </div>
              )}
              {!me.queue && <p className="mt-2 text-[10px] text-purple-200/60">Live fechada? Pode clicar mesmo assim: assim que você abrir a live no KAKO, nossa conta entra, agradece no chat e entrega.</p>}
            </>
          )}
        </div>

        {/* painel social (padrão Roleta das Azulzinhas): letreiro rolando + abas em pílula */}
        <div className="mt-5 rounded-2xl border border-fuchsia-500/40 bg-[#140a2e]/85 p-3 backdrop-blur" data-testid="rp-social-panel">
          <RpWinnersMarquee recent={state.recent} />
          <div className="mb-3 flex gap-1 rounded-full bg-purple-950/70 p-1" data-testid="rp-tabs">
            {[['feed', 'Ganhadores ao vivo', Radio], ['top', 'Top 10 Sortudos', Trophy]].map(([k, label, Icon]) => (
              <button key={k} onClick={() => { if (tab !== k) sfx.tab(); setTab(k); }} data-testid={`rp-tab-${k}`} aria-selected={tab === k}
                className={`flex flex-1 items-center justify-center gap-1 rounded-full px-3 py-1.5 text-xs font-bold transition-colors ${tab === k ? 'bg-fuchsia-500 text-white shadow-md shadow-fuchsia-500/40' : 'text-purple-300 hover:text-white'}`}>
                <Icon className="h-3.5 w-3.5" /> {label}
              </button>
            ))}
          </div>

          <div className={tab === 'top' ? '' : 'hidden'}><RpTopWinners top={state.top} /></div>
          <div className={tab === 'feed' ? '' : 'hidden'}><RpWinnersFeed me={me} tick={feedTick} /></div>
        </div>
      </div>
    </Shell>
  );
}
